import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

/**
 * SuccessBurst — fullscreen celebratory overlay for win moments
 * (deposit confirmed, account created, bonus claimed). Renders a
 * tier-colored ring expanding outward, a checkmark with bounce, a
 * tier-or-amount label, and a shower of coin sparks ejecting from the
 * center. Auto-dismisses after `duration` ms (default 1.8s).
 *
 * Props:
 *   open         boolean — show/hide
 *   title        string  — main label, e.g. "Deposit confirmed"
 *   subtitle     string  — optional secondary text, e.g. "$9.99 added"
 *   color        string  — accent color (defaults to gold)
 *   icon         element  — replace the checkmark with custom icon
 *   duration     number  — total visible ms (default 1800)
 *   onDone       fn      — called when overlay dismisses
 *   showCoins    boolean — emit coin sparks (default true)
 */
export default function SuccessBurst({
  open,
  title = 'Success',
  subtitle = '',
  color = '#D4AF37',
  icon: IconC,
  duration = 1800,
  onDone,
  showCoins = true,
  'data-testid': testId = 'success-burst',
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMounted(true);
    const t = setTimeout(() => { setMounted(false); onDone?.(); }, duration);
    return () => clearTimeout(t);
  }, [open, duration, onDone]);

  if (!open || !mounted) return null;

  // Coin shower — 18 sparks ejecting in random directions
  const sparks = Array.from({ length: 18 }, (_, i) => {
    const angle = (i / 18) * Math.PI * 2;
    const dist = 110 + Math.random() * 80;
    return {
      id: i,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      delay: Math.random() * 0.15,
      size: 5 + Math.random() * 4,
    };
  });

  const Icon = IconC || CheckCircle2;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none"
      data-testid={testId}
    >
      {/* Backdrop dim with quick fade */}
      <div
        className="absolute inset-0 anim-backdrop-fade"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' }}
      />

      {/* Expanding ring */}
      <div
        className="absolute rounded-full anim-success-ring pointer-events-none"
        style={{
          width: 140, height: 140,
          border: `3px solid ${color}`,
          boxShadow: `0 0 40px ${color}aa`,
        }}
      />

      {/* Inner badge */}
      <div className="relative flex flex-col items-center gap-3 anim-success-burst" data-testid={`${testId}-card`}>
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center"
          style={{
            background: `radial-gradient(circle, ${color}33 0%, ${color}10 60%, transparent 100%)`,
            border: `2px solid ${color}`,
            boxShadow: `0 0 24px ${color}aa, inset 0 0 20px ${color}33`,
          }}
        >
          <Icon className="w-12 h-12" style={{ color, filter: `drop-shadow(0 0 6px ${color})` }} strokeWidth={2.5} />
        </div>
        <p
          className="text-lg font-black text-white text-center"
          style={{ textShadow: `0 0 12px ${color}, 0 4px 8px rgba(0,0,0,0.6)` }}
        >
          {title}
        </p>
        {subtitle && (
          <p className="text-sm text-gray-200 text-center -mt-2 font-semibold tabular-nums">{subtitle}</p>
        )}
      </div>

      {/* Coin sparks */}
      {showCoins && sparks.map(s => (
        <div
          key={s.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: '50%', left: '50%',
            width: s.size, height: s.size,
            background: s.id % 2 === 0 ? color : '#FFD700',
            boxShadow: `0 0 8px ${color}`,
            '--dx': `${s.dx}px`,
            '--dy': `${s.dy}px`,
            animation: `sb-spark 1.1s cubic-bezier(0.22, 0.8, 0.27, 1) ${s.delay}s both`,
          }}
        />
      ))}

      <style>{`
        @keyframes sb-spark {
          0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
          25% { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1.1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
